import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import styled from 'styled-components';
import { Link } from '@mui/material';
import { GridColDef, GridPreProcessEditCellProps, useGridApiRef } from '@mui/x-data-grid-pro';
import { GridValidRowModel } from '@mui/x-data-grid/models/gridRows';

import { newGridRecordId } from '@shared/commonFunctions';
import { dataGridCol, gridCurrencyCell, gridStateCell } from '@shared/components/grid/common';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { GridColTextInput } from '@shared/components/grid/GridColTextInput';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';
import { GridColInvertedBoolean } from '@shared/components/grid/GridColInvertedBoolean';
import { calculationType, options } from '@modules/admin/Tools/LenderUtility/tabs/DocFees/DocFeesTable/interfaces';
import { productTypes } from '@modules/admin/Tools/LenderUtility/shared/data';

import { IStateProductRestriction } from '../interfaces';
import { stateProductRestrictionSchema } from '../validation';
import {
  requestCreateStateProductRestriction,
  requestDeleteStateProductRestriction,
  requestUpdateStateProductRestriction,
} from './requests';
import { ProductRestrictionFormulaModal } from './ProductRestrictionsFormulaModal';

const StyledGridWrapper = styled.div`
  display: flex;
  padding-left: 20px;
  padding-right: 20px;
  width: 100%;
  flex-direction: column;
  .MuiOutlinedInput-root {
    padding-top: 0px !important;
  }

  .MuiSelect-select {
    height: 2rem !important;
  }
`;

const newRowModel = {
  isNew: true,
};

const preProcessAmount = ({ props, otherFieldsProps }: GridPreProcessEditCellProps) => ({
  ...props,
  value: otherFieldsProps?.calculationType.value !== calculationType.Formula ? props.value : null,
});

const preProcessFormula = ({ props, otherFieldsProps }: GridPreProcessEditCellProps) => ({
  ...props,
  value: otherFieldsProps?.calculationType.value !== calculationType.FlatFee ? props.value : null,
});

export const StateProductRestrictions = () => {
  const apiRef = useGridApiRef();

  const [modalData, setModalData] = useState<IStateProductRestriction | null>(null);
  const [loading, setLoading] = useState(false);
  const { data: stateProductRestrictions = [], mutate: stateProductRestrictionsMutate } = useSWR<IStateProductRestriction[]>(
    `${SERVICES.PRODUCT}/producttypes/getall-producttypes-staterules`
  );

  const handleChange = useCallback((value: unknown, row: IStateProductRestriction) => {
    if (value === calculationType.Formula) {
      setModalData(row);
    }
  }, []);

  const columns: GridColDef[] = useMemo(
    () =>
      [
        dataGridCol('state', gridStateCell),
        dataGridCol('productType', {
          flex: 1,
          minWidth: 150,
          editable: true,
          renderEditCell: (params) => (
            <GridColSelect
              options={productTypes}
              params={params}
            />
          ),
        }),
        dataGridCol('calculationType', {
          headerName: 'FlatFee/Formula',
          width: 150,
          renderEditCell: (params) => {
            const updatedParams = {
              ...params,
              value:
                typeof params.value == 'string' ? calculationType[params.value as keyof typeof calculationType] : params.value,
            };

            return (
              <GridColSelect
                hideNoneOption
                isSearchable
                disabled={!params.row.state}
                options={options}
                params={updatedParams}
                onChange={(value) => handleChange(value, params.row)}
              />
            );
          },
          editable: true,
        }),
        dataGridCol('maxAllowedAmount', {
          ...gridCurrencyCell,
          headerName: 'Amount',
          minWidth: 200,
          textWrap: true,
          preProcessEditCellProps: preProcessAmount,
          renderCell: (params) => {
            const isFormulaField = params.row.calculationType === calculationType[calculationType.Formula];
            if (isFormulaField) return <Link onClick={() => setModalData(params.row)}>{params.row.maxAllowedFormula}</Link>;
            return params.value;
          },
          renderEditCell: (params) => {
            const type =
              typeof params.row.calculationType == 'string'
                ? calculationType[params.row.calculationType as keyof typeof calculationType]
                : params.row.calculationType;

            const isFormula = type === calculationType.Formula;
            const formulaParams = {
              ...params,
              value: params.row.maxAllowedFormula,
            };

            return (
              <>
                {!isFormula ? (
                  <GridColNumber
                    disabled={!params.row.state}
                    params={params}
                    prefix="$"
                    decimalScale={2}
                  />
                ) : (
                  <GridColTextInput
                    disabled
                    params={formulaParams}
                  />
                )}
              </>
            );
          },
          flex: 1,
          editable: true,
        }),
        dataGridCol('maxAllowedFormula', {
          hide: true,
          editable: true,
          preProcessEditCellProps: preProcessFormula,
        }),
        dataGridCol('isAllowed', {
          headerName: 'Exclude State',
          minWidth: 150,
          type: 'boolean',
          editable: true,
          renderCell: (params) => {
            const display = params.value ? 'No' : 'Yes';
            return display;
          },
          renderEditCell: (params) => {
            return <GridColInvertedBoolean params={params} />;
          }
        }),
      ].map((column) => ({ ...column, minWidth: 80 })),
    [handleChange, preProcessAmount, preProcessFormula]
  );

  const gridActions = {
    onSave: async (row: IStateProductRestriction) => {
      if (Object.prototype.hasOwnProperty.call(row, 'id')) {
        setLoading(true);
        let response;
        const formattedCalculationType =
          typeof row.calculationType === 'string'
            ? Number(calculationType[row.calculationType as keyof typeof calculationType])
            : row.calculationType;
        if (row.id === newGridRecordId) {
          const requestPayload = {
            name: row.productType,
            state: row.state,
            maxAllowedAmount: row.maxAllowedAmount,
            maxAllowedFormula: row.maxAllowedFormula,
            isAllowed: row.isAllowed  ?? true,
            calculationType: formattedCalculationType,
          };
          response = await requestCreateStateProductRestriction(requestPayload);
        } else {
          const updateRequestPayload = {
            id: row.id,
            name: row.productType,
            state: row.state,
            maxAllowedAmount: row.maxAllowedAmount,
            maxAllowedFormula: row.maxAllowedFormula,
            isAllowed: row.isAllowed,
            calculationType: formattedCalculationType,
          };
          response = await requestUpdateStateProductRestriction(updateRequestPayload);
        }
        await stateProductRestrictionsMutate();
        setLoading(false);
        return response;
      }
    },
    onDelete: async (row: GridValidRowModel) => {
      setLoading(true);
      const resp = await requestDeleteStateProductRestriction(row?.id);
      await stateProductRestrictionsMutate();
      setLoading(false);

      return resp;
    },
    onCancel: async () => {
      setLoading(true);
      await stateProductRestrictionsMutate();
      setLoading(false);
    },
  };

  return (
    <>
      {!!modalData && (
        <ProductRestrictionFormulaModal
          apiRef={apiRef}
          modalData={modalData}
          productId={modalData.id}
          setLoading={setLoading}
          loading={loading}
          stateProductRestrictionsMutate={stateProductRestrictionsMutate}
          handleClose={() => setModalData(null)}
        />
      )}
      <StyledGridWrapper>
        <PhoenixGrid
          key={stateProductRestrictions?.length}
          autoHeight
          withHeader
          canNotCopy
          disableSelectionOnClick
          isFlexible
          autoRowHeight
          apiRef={apiRef}
          name="State Product Restriction"
          height="auto"
          buttonTitle="Add New"
          gridId={'State Product Restriction'}
          pageSize={5}
          columns={columns}
          loading={!stateProductRestrictions}
          rows={stateProductRestrictions}
          actions={gridActions}
          newRowModel={newRowModel}
          validateSchema={stateProductRestrictionSchema}
        />
      </StyledGridWrapper>
    </>
  );
};
